import { isFinite } from 'lodash';
import { QueryClient } from 'react-query';
import {
  addQueryToUrl,
  hasChangeThumb,
} from 'components/VideoPlayer/Transcription/MediaUtilities';
import { MediaService } from 'services';
import { Duration } from 'utils/constants';
import { ILibraryItem } from 'utils/models';

export const HOVER_PLAY_DELAY_MS = 200;
// play_start_ms -> match_end_ms is mostly the engine's fixed 1.5 s lead-in
// (single-word matches last 40-60 ms), and passages run from ~2.5 s to ~43 s,
// so every hover plays between these two lengths.
export const MIN_PLAY_MS = 10000;
export const MAX_PLAY_MS = 20000;
// GET /media/preview URLs are presigned for 12 h. Refetch well before expiry.
export const PREVIEW_URL_STALE_MS = Duration.hours(1);

const isTime = (ms?: number): ms is number => isFinite(ms) && ms! >= 0;

export const resolveSemanticPlayWindow = (
  item: ILibraryItem,
  isFilterOnly = false,
): { startSec: number; endSec: number } | null => {
  const start = item.play_start_ms;
  if (!isTime(start)) return null;
  if (isFilterOnly) return { startSec: 0, endSec: MIN_PLAY_MS / 1000 };

  const matchEnd = isTime(item.match_end_ms) ? item.match_end_ms : start;
  const chunkEnd = isTime(item.chunk_end_ms) ? item.chunk_end_ms : start;
  // The match can end at or after chunk_end_ms, so chunk_end_ms is not a cap.
  const naturalEnd = Math.max(matchEnd, chunkEnd);
  const end = Math.max(
    Math.min(Math.max(naturalEnd, start + MIN_PLAY_MS), start + MAX_PLAY_MS),
    matchEnd, // never cut the matched words
  );

  return { startSec: start / 1000, endSec: end / 1000 };
};

const AUDIO_EXTENSIONS = new Set([
  'mp3',
  'aac',
  'au',
  'flac',
  'm4a',
  'wav',
  'amr',
  'aiff',
  'ra',
  'wma',
  'ogg',
  'caf',
]);

const extensionOf = (filename?: string): string =>
  filename?.split('.').pop()?.toLowerCase() ?? '';

// Some uploads are stored as application/octet-stream, so the extension is
// the fallback when the type is not a real audio/* or video/* type.
const realMediaType = (item: ILibraryItem): string | undefined => {
  const type = item.mediacontenttype?.toLowerCase();
  return type && /^(audio|video)\//.test(type) ? type : undefined;
};

export const isAudioItem = (item: ILibraryItem): boolean => {
  const type = realMediaType(item);
  return type
    ? type.startsWith('audio/')
    : AUDIO_EXTENSIONS.has(extensionOf(item.filename));
};

/** Where the "playing" ring goes on an audio card: behind the corner
 * microphone badge (custom thumbnail), or over the microphone drawn in the
 * centre of the default AUDIO_THUMBNAIL image (no badge). */
export const audioIndicatorPosition = (
  item: ILibraryItem,
): 'corner' | 'center' => (hasChangeThumb(item) ? 'corner' : 'center');

// No status gate: a searchable result may preview. GET /media/preview decides
// which statuses it serves a URL for.
export const canHoverPlay = (item: ILibraryItem): boolean =>
  isTime(item.play_start_ms);

// A filter-only search has no matched passage, so the transcript opens at the
// start, the same place the hover preview plays from.
export const withSemanticTime = (
  path: string,
  item: ILibraryItem,
  isFilterOnly = false,
): string =>
  !isFilterOnly && isTime(item.play_start_ms)
    ? addQueryToUrl(path, `time=${item.play_start_ms / 1000}`)
    : path;

/** Presigned media URL for hover-play, cached per media so several cards for
 * the same media share one GET /media/preview. */
export const fetchPreviewUrl = (
  queryClient: QueryClient,
  mediaid: string,
): Promise<string> =>
  queryClient.fetchQuery(
    ['media-preview-url', mediaid],
    async () =>
      (await MediaService.getPreview({ mediaid, hoverPreview: true })).data
        .url,
    { staleTime: PREVIEW_URL_STALE_MS, cacheTime: PREVIEW_URL_STALE_MS },
  );

export type HoverPlayJob = {
  loadUrl: () => Promise<string>;
  getElement: () => HTMLMediaElement | null;
  startSec: number;
  endSec: number;
  onWarn: (reason: string) => void;
};

/** Hover timer and stale-hover guard, kept outside React so it can be tested
 * (react 18 with react-dom 17 cannot render components under Jest). */
export const createHoverPlayback = (delayMs = HOVER_PLAY_DELAY_MS) => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  // Bumped on every enter/leave, so a URL that resolves after the pointer
  // left (or re-entered) is dropped instead of played.
  let hoverId = 0;
  // The URL the element currently holds, so a re-hover seeks the buffered
  // media instead of downloading it again.
  let loadedUrl: string | undefined;

  const play = async (id: number, job: HoverPlayJob) => {
    let url: string;
    try {
      url = await job.loadUrl();
    } catch {
      job.onWarn('hover preview unavailable');
      return;
    }

    const el = job.getElement();
    if (id !== hoverId || !el) return;

    if (url === loadedUrl && el.hasAttribute('src')) {
      el.currentTime = job.startSec;
    } else {
      // The #t end only applies to this first load. Callers pause on
      // timeupdate at endSec, which also covers re-hovers.
      el.src = `${url}#t=${job.startSec},${job.endSec}`;
      loadedUrl = url;
    }
    el.play().catch(() => job.onWarn('hover play rejected'));
  };

  const cancel = () => {
    hoverId += 1;
    if (timer) clearTimeout(timer);
  };

  return {
    enter(job: HoverPlayJob) {
      cancel();
      const id = hoverId;
      timer = setTimeout(() => {
        void play(id, job);
      }, delayMs);
    },
    // ponytail: keeps the source and buffer of every hovered card until it
    // unmounts, so memory grows with distinct cards hovered on one results
    // page (at most 30). If that bites, release after an idle timeout or keep
    // only the last N hovered cards.
    leave(el: HTMLMediaElement | null) {
      cancel();
      el?.pause();
    },
    release(el: HTMLMediaElement | null) {
      cancel();
      loadedUrl = undefined;
      if (!el) return;
      el.pause();
      el.removeAttribute('src');
      el.load(); // aborts the in-flight media download
    },
    cancel,
  };
};
